import styles from './SearchBar.less';

import React, { Component, PropTypes } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import pick from 'lodash.pick';
import IsolatedScroll from 'react-isolated-scroll';

import {
  SearchIcon,
  ChevronIcon,
  HelpIcon,
  Button,
  TextField,
  ClearField
} from 'seek-style-guide/react';

import {
  updateKeywordsField,
  whereSuggestionSelected,
  searchSubmit,
  selectClassification,
  selectWhere,
  searchHelpClicked,
  updateActiveRefinement,
  clearActiveRefinement
} from 'store/search/search';

import { qualifyUrl } from '@seek/seek-jobs-seo';
import QualifiedLink from 'QualifiedLink/QualifiedLink';
import { ScreenReaderOnly } from 'seek-style-guide/react';
import ScreenReaderSkipTarget from 'Accessibility/ScreenReaderSkipTarget';
import ClassificationList from 'ClassificationList/ClassificationList';
import WhereField from 'WhereField/WhereField';
import LastSearch from 'LastSearch/LastSearch';
import MoreOptions from 'MoreOptions/MoreOptions';
import ExternalLink from 'ExternalLink/ExternalLink';

const clean = object => pick(object, x => x);

const { func, string, shape, array, bool, object } = PropTypes;

function mapStateToProps({ search, location, lastSearch }) {
  return {
    keywordsField: search.keywordsField,
    whereField: search.whereField,
    query: search.query,
    hasLastSearch: Boolean(lastSearch.query),
    classifications: search.refinements.classification,
    clearClassification: search.refinements.clear.classification,
    locale: location.locale,
    isHomepage: location.isHomepage,
    classificationListOpen: search.activeRefinement === 'Classification'
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onSubmit: () => dispatch(searchSubmit()),
    updateKeywordsField: value => dispatch(updateKeywordsField(value)),
    whereSuggestionSelected: suggestion => dispatch(whereSuggestionSelected(suggestion)),
    selectClassification: classification => dispatch(selectClassification(classification)),
    selectWhere: where => dispatch(selectWhere(where)),
    onHelpClick: () => dispatch(searchHelpClicked()),
    openClassificationList: () => dispatch(updateActiveRefinement('Classification')),
    closeClassificationList: () => dispatch(clearActiveRefinement())
  };
}

@withRouter
@connect(mapStateToProps, mapDispatchToProps)
export default class SearchBar extends Component {

  static propTypes = {
    keywordsField: string.isRequired,
    whereField: string.isRequired,
    query: shape({
      keywords: string,
      where: string,
      classification: string,
      subclassification: string
    }).isRequired,
    hasLastSearch: bool.isRequired,
    classifications: array.isRequired,
    collapsed: bool.isRequired,
    selectClassification: func.isRequired,
    clearClassification: object,
    onSubmit: func.isRequired,
    updateKeywordsField: func.isRequired,
    whereSuggestionSelected: func.isRequired,
    selectWhere: func.isRequired,
    onHelpClick: func.isRequired,
    locale: string.isRequired,
    isHomepage: bool.isRequired,
    router: object.isRequired,
    classificationListOpen: bool.isRequired,
    openClassificationList: func.isRequired,
    closeClassificationList: func.isRequired
  };

  constructor() {
    super();

    this.whereDebouncedUpdateSearchQuery = debounce(this.updateSearchQuery, 50);
    this.keywordsDebouncedUpdateSearchQuery = debounce(this.updateSearchQuery, 50);
    this.onSubmit = this.onSubmit.bind(this);
    this.onKeywordsChange = this.onKeywordsChange.bind(this);
    this.onKeywordsBlur = this.onKeywordsBlur.bind(this);
    this.onKeywordsClear = this.onKeywordsClear.bind(this);
    this.onWhereSuggestionSelected = this.onWhereSuggestionSelected.bind(this);
    this.onWhereBlur = this.onWhereBlur.bind(this);
    this.onWhereClear = this.onWhereClear.bind(this);
    this.onClassificationListKeyUp = this.onClassificationListKeyUp.bind(this);
    this.onClearClassificationClick = this.onClearClassificationClick.bind(this);
    this.toggleClassificationList = this.toggleClassificationList.bind(this);
    this.storeSubmitButtonReference = this.storeSubmitButtonReference.bind(this);
    this.storeClassificationsToggleReference = this.storeClassificationsToggleReference.bind(this);
  }

  storeClassificationsToggleReference(classificationsToggle) {
    if (classificationsToggle !== null) {
      this.classificationsToggle = classificationsToggle;
    }
  }

  storeSubmitButtonReference(submitButton) {
    if (submitButton !== null) {
      this.submitButton = submitButton.button;
    }
  }

  onKeywordsChange(event) {
    this.props.updateKeywordsField(event.target.value);
  }

  onKeywordsClear() {
    this.props.updateKeywordsField('');
    this.keywordsDebouncedUpdateSearchQuery();
  }

  onKeywordsBlur(event) {
    const { keywords = '' } = this.props.query;
    const { value } = event.target;

    if (value !== keywords) {
      this.keywordsDebouncedUpdateSearchQuery();
    }
  }

  onSubmit(event) {
    event.preventDefault();

    this.submitButton.focus();
    this.props.onSubmit();
    this.whereDebouncedUpdateSearchQuery.cancel();
    this.keywordsDebouncedUpdateSearchQuery.cancel();
    this.updateSearchQuery(this.props.query, '/jobs');
  }

  updateSearchQuery(newQuery = this.props.query, newPath) {
    const { locale, isHomepage, keywordsField, whereField, router } = this.props;
    const defaultPath = isHomepage ? '/' : '/jobs';

    const finalQuery = clean({
      ...newQuery,
      keywords: keywordsField,
      where: whereField,
      page: null, // Reset page parameter
      sortmode: keywordsField.trim() ? newQuery.sortmode : null
    });

    const { path: pathname, query } = qualifyUrl(locale, newPath || defaultPath, finalQuery);

    router.push({ pathname, query });
  }

  onWhereSuggestionSelected({ suggestion, suggestionValue }) {
    this.props.whereSuggestionSelected({ suggestion, suggestionValue });
    this.whereDebouncedUpdateSearchQuery();
  }

  onWhereClear() {
    this.whereDebouncedUpdateSearchQuery();
  }

  onWhereBlur(event) {
    const { where = '' } = this.props.query;

    if (event.target.value !== where) {
      this.whereDebouncedUpdateSearchQuery();
    }
  }

  onClassificationListKeyUp(event) {
    const ESCAPE_KEYCODE = 27;
    if (event.keyCode === ESCAPE_KEYCODE) {
      this.classificationsToggle.click();
      this.classificationsToggle.focus();
    }
  }

  onClearClassificationClick(event) {
    event.stopPropagation();
  }

  toggleClassificationList() {
    const { classificationListOpen, closeClassificationList, openClassificationList } = this.props;

    if (classificationListOpen) {
      closeClassificationList();
    } else {
      openClassificationList();
    }
  }

  render() {
    const {
      keywordsField,
      query,
      hasLastSearch,
      classifications,
      collapsed,
      clearClassification,
      onHelpClick,
      selectClassification: onClassficationSelect,
      classificationListOpen,
      closeClassificationList
    } = this.props;

    const { keywords } = query;
    const selectedClassifications = classifications.filter(item => item.isActive);
    const refinePath = (collapsed ? '/jobs' : '/');
    const rootClassName = classnames({
      [styles.root_isCollapsed]: collapsed,
      [styles.root_hasLastSearch]: hasLastSearch
    });
    const classificationGroupClasses = classnames({
      [styles.listItemField]: true,
      [styles.classificationsGroup]: true,
      [styles.classificationsGroup_hasSelections]: clearClassification
    });

    return (
      <section className={rootClassName}>
        <ScreenReaderOnly>
          <h1>Perform a search</h1>
        </ScreenReaderOnly>

        <div className={styles.formContainer}>
          <form
            role="search"
            name="SearchBar"
            id="SearchBar"
            className={styles.form}
            method="get"
            action="/jobs"
            onSubmit={this.onSubmit}>

            <input type="hidden" name="classification" value={query.classification || ''} />
            <input type="hidden" name="subclassification" value={query.subclassification || ''} />

            <div className={styles.helpItem}>
              <ExternalLink
                analytics="search+help"
                className={styles.helpLink}
                target="_blank"
                rel="nofollow noopener noreferrer"
                href="/content/help/SearchHelp.pdf"
                title="Help"
                intercept={onHelpClick}>
                <HelpIcon svgClassName={styles.helpSvg} />
              </ExternalLink>
            </div>

            <ScreenReaderSkipTarget name="start-of-content" />

            <div className={styles.list}>
              <div className={`${styles.listItem} ${styles.listItem_isWhat}`}>
                <div className={styles.listItemGroup}>
                  <div className={styles.listItemField}>
                    <TextField
                      id="SearchBar__Keywords"
                      className={styles.keywordsField}
                      label="What"
                      labelProps={{ className: styles.listItemLabel }}
                      inputProps={{
                        name: 'keywords',
                        type: 'search',
                        value: keywordsField,
                        className: styles.keywordsInput,
                        maxLength: '500',
                        placeholder: 'Enter keywords',
                        autoComplete: 'off',
                        onChange: this.onKeywordsChange,
                        onBlur: this.onKeywordsBlur,
                        'data-automation': 'searchKeywordsField',
                        'aria-label': 'Keywords'
                      }}
                      onClear={this.onKeywordsClear}
                    />
                  </div>

                  <div className={classificationGroupClasses}>
                    <input
                      id="SearchBar__Classifications"
                      className={styles.classificationsToggle}
                      type="checkbox"
                      checked={classificationListOpen}
                      ref={this.storeClassificationsToggleReference}
                    />

                    <div className={styles.classificationsBackdrop}>
                      <label
                        data-automation="classificationDropDownListBackdrop"
                        htmlFor="SearchBar__Classifications"
                        className={styles.classificationsBackdropLabel}
                        onClick={closeClassificationList}>
                        <ScreenReaderOnly>Hide classifications</ScreenReaderOnly>
                      </label>
                    </div>

                    <label
                      data-automation="classificationDropDownList"
                      htmlFor="SearchBar__Classifications"
                      className={`${styles.field} ${styles.field_isClassifications}`}
                      onClick={this.toggleClassificationList}>
                      <ScreenReaderOnly>Show classification list. </ScreenReaderOnly>
                      <span className={styles.classificationsText}>
                        {
                          {
                            0: 'Any Classification',
                            1: selectedClassifications[0] ? selectedClassifications[0].label : ''
                          }[selectedClassifications.length] || `${selectedClassifications.length} Classifications`
                        }
                      </span>

                      <div className={styles.chevronContainer}>
                        <ChevronIcon direction="down" className={styles.chevron} svgClassName={styles.chevronSvg} />
                      </div>

                      <QualifiedLink
                        title="Clear classifications"
                        className={styles.clearClassificationLink}
                        path={refinePath}
                        params={clearClassification || query}
                        aria-hidden={!clearClassification}
                        aria-label="Clear classifications"
                        tabIndex={clearClassification ? 0 : -1}
                        data-automation="clearAllClassifications"
                        rel="nofollow"
                        onClick={this.onClearClassificationClick}>
                        <ClearField />
                      </QualifiedLink>
                    </label>

                    <IsolatedScroll className={styles.classificationsPanel}>
                      <ClassificationList
                        onKeyUp={this.onClassificationListKeyUp}
                        handleSelection={onClassficationSelect}
                        path={refinePath}
                        classifications={classifications}
                        open={classificationListOpen}
                      />
                    </IsolatedScroll>
                  </div>
                </div>

              </div>

              <div className={styles.listItem}>
                <WhereField
                  id="SearchBar__Where"
                  name="where"
                  label="Where"
                  labelProps={{ className: styles.listItemLabel }}
                  onSuggestionSelected={this.onWhereSuggestionSelected}
                  onInputBlur={this.onWhereBlur}
                  onClear={this.onWhereClear}
                />
              </div>
            </div>

            <Button
              color="pink"
              type="submit"
              data-automation="searchButton"
              className={styles.button}
              aria-label="Submit search"
              ref={this.storeSubmitButtonReference}>
              SEEK
            </Button>
          </form>

          <div className={styles.searchOptions}>
            <div className={styles.lastSearch} aria-hidden={collapsed}>
              { hasLastSearch && <LastSearch /> }
            </div>

            <div className={styles.moreOptions} aria-hidden={collapsed}>
              <MoreOptions />
            </div>
          </div>
        </div>

        <div className={styles.summary}>
          <QualifiedLink path="/" params={query} className={styles.summaryLink}>
            <SearchIcon className={styles.magnifyingGlass} svgClassName={styles.magnifyingGlassSvg} />
            { keywords || 'Jobs' }
            {' in '}
            { selectedClassifications.length > 0 ? selectedClassifications[0].label : 'All Classifications' }
          </QualifiedLink>
        </div>

        <div className={styles.backdrop} />
      </section>
    );
  }

}



/** WEBPACK FOOTER **
 ** ./app/components/Search/components/SearchBar/SearchBar.js
 **/